import {
  normalizeRawTransaction,
  type RawTransaction,
} from './raw-transaction';

/**
 * Accept a raw object, an ordered raw array, or an encoded Safe export.
 * Validate every entry before returning any children, so a bad later entry
 * cannot leave a partially imported batch. Native sends retain empty calldata.
 */
export function parseTransactionJson(
  contents: string,
  selectedNetworkId?: number
): [RawTransaction, ...RawTransaction[]] {
  let parsed: unknown;
  // Case: malformed JSON rejects the file before any children enter the draft.
  try {
    parsed = JSON.parse(contents);
  } catch {
    throw new Error('File contents must be valid JSON.');
  }

  let entries: unknown[];
  let isSafeExport = false;
  // Case: a Safe export wraps its ordered transactions with a shared chain identifier.
  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    'transactions' in parsed
  ) {
    isSafeExport = true;
    const file = parsed as Record<string, unknown>;
    // Case: the Safe export must identify its chain with a decimal string.
    if (typeof file.chainId !== 'string' || !/^\d+$/.test(file.chainId)) {
      throw new Error('Safe transaction JSON must include a decimal chainId.');
    }
    const chainId = Number(file.chainId);
    // Case: reject chain identifiers that cannot be compared exactly with the selected network.
    if (!Number.isSafeInteger(chainId) || chainId <= 0) {
      throw new Error('Safe transaction JSON contains an invalid chainId.');
    }
    // Case: importing a Safe export must not change the parent's selected network.
    if (selectedNetworkId !== undefined && chainId !== selectedNetworkId) {
      throw new Error(
        `This transaction is for chain ${chainId}, but the selected network is chain ${selectedNetworkId}.`
      );
    }
    // Case: a Safe wrapper must contain a transaction list, not a single payload object.
    if (!Array.isArray(file.transactions)) {
      throw new Error(
        'Safe transaction JSON must contain a transactions array.'
      );
    }
    entries = file.transactions;
  } else {
    entries = Array.isArray(parsed) ? parsed : [parsed];
  }

  // Case: an empty file cannot produce a valid transaction parent.
  if (entries.length === 0) {
    throw new Error('Transaction JSON must contain at least one transaction.');
  }
  const transactions = entries.map((entry, index) => {
    // Case: any invalid child rejects the whole file and reports its original row number.
    try {
      // Case: raw entries use the strict payload format without Safe export metadata.
      if (!isSafeExport) return normalizeRawTransaction(entry);
      // Case: each Safe transaction must be an object before its payload can be inspected.
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        throw new Error('The Safe transaction must be an object.');
      }
      const record = entry as Record<string, unknown>;
      // Case: Safe delegatecalls cannot be executed as ordinary batch children.
      if (record.operation !== undefined && record.operation !== 0) {
        throw new Error('Safe delegatecall transactions are not supported.');
      }
      // Safe display metadata is ignored; authority-bearing overrides must
      // never be silently dropped when selecting a shared source and network.
      // Case: a child attempts to override the parent's account or network.
      if (
        [
          'from',
          'chainId',
          'networkId',
          'accountId',
          'multichainAccountId',
        ].some((key) => key in record)
      ) {
        throw new Error(
          'Source account and network must come from the batch, not individual transactions.'
        );
      }
      // Case: imported function descriptions cannot substitute for encoded calldata.
      if (typeof record.data !== 'string') {
        throw new Error(
          'The Safe transaction must include encoded data. contractMethod-only files are not supported.'
        );
      }
      return normalizeRawTransaction({
        to: record.to,
        value: record.value,
        data: record.data,
      });
    } catch (error) {
      throw new Error(
        `Transaction ${index + 1}: ${error instanceof Error ? error.message : 'Invalid transaction.'}`
      );
    }
  });
  const [first, ...rest] = transactions;
  // Case: returning a completed draft requires a first validated child.
  if (!first)
    throw new Error('Transaction JSON must contain at least one transaction.');
  return [first, ...rest];
}
